import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, ExternalLink } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { toast } from '@/hooks/use-toast';
import { apiUrl } from '@/lib/api-url';
import {
  detectInAppBrowser,
  getCurrentPageUrl,
  inAppBrowserDisplayName,
  isAndroid,
  isIos,
  targetBrowserName,
  type InAppBrowserKind,
} from '@/lib/inAppBrowser';
import { detectPlatform, isInstalledPWA } from '@/lib/pushPromptAnalytics';

interface InAppBrowserOverlayProps {
  /**
   * When true, render as a soft top-banner that the user can dismiss to keep
   * using the app. When false (default), render as a full-screen blocker.
   */
  soft?: boolean;
}

const SESSION_DISMISS_KEY = 'papacito_inapp_overlay_dismissed';
// We re-use the push prompt event endpoint with a new event_type. If the
// backend hasn't been expanded to know these event types yet, the request
// will 400 and we swallow — UX must never break.
const AMBIENT_VARIANT = 'D_welcome';

type InAppEventType =
  | 'inapp_browser_detected'
  | 'inapp_browser_copy_link'
  | 'inapp_browser_dismissed';

async function fireEvent(
  token: string | null | undefined,
  eventType: InAppEventType,
  kind: InAppBrowserKind,
): Promise<void> {
  if (!token) return;
  try {
    await fetch(apiUrl('/api/push/prompt-event'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        variant: AMBIENT_VARIANT,
        eventType,
        platform: detectPlatform(),
        isInstalled: isInstalledPWA(),
        contextMetadata: { inAppBrowser: kind },
        gateReason: kind ?? null,
      }),
      keepalive: true,
    });
  } catch {
    /* swallow — analytics must never break UX */
  }
}

function getInstructionLines(kind: InAppBrowserKind): string[] {
  const ios = isIos();
  const android = isAndroid();
  const browser = targetBrowserName();

  if (kind === 'tiktok') {
    return [
      'Tap the share icon (paper plane)',
      `Tap "Open in ${browser}"`,
      `Continue here in ${browser}`,
    ];
  }
  if (kind === 'facebook' || kind === 'instagram' || kind === 'snapchat') {
    if (ios) {
      return [
        'Tap the ••• menu (top right)',
        'Tap "Open in External Browser" or "Open in Safari"',
        'Continue here in Safari',
      ];
    }
    if (android) {
      return [
        'Tap the ••• menu (top right)',
        'Tap "Open in Chrome"',
        'Continue here in Chrome',
      ];
    }
  }
  // Generic fallback — Twitter / LinkedIn / Pinterest / unknown.
  return [
    'Tap the ••• menu (usually top right)',
    `Tap "Open in ${browser}"`,
    `Continue here in ${browser}`,
  ];
}

export function InAppBrowserOverlay({ soft = false }: InAppBrowserOverlayProps) {
  const { token } = useAuth();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(SESSION_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const kind = useMemo<InAppBrowserKind>(() => detectInAppBrowser(), []);
  const visible = !!kind && !dismissed;

  // Fire detection analytics once per session when the overlay first becomes
  // visible. We tolerate the user being unauthenticated — the event will only
  // post when there's a token; that's fine, in-app browser bleed mostly hits
  // post-signup users anyway, but we don't want to lose pre-auth signal so we
  // also retry on token mount.
  useEffect(() => {
    if (!visible || !kind) return;
    void fireEvent(token, 'inapp_browser_detected', kind);
  }, [visible, kind, token]);

  if (!visible || !kind) return null;

  const browserName = targetBrowserName();
  const appName = inAppBrowserDisplayName(kind);

  const handleCopyLink = async () => {
    const url = getCurrentPageUrl();
    let copied = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        copied = true;
      }
    } catch {
      /* fall through to legacy fallback */
    }
    if (!copied) {
      // Legacy fallback for older WebViews that block clipboard API.
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        copied = true;
      } catch {
        /* ignore */
      }
    }
    if (copied) {
      toast({
        title: 'Link copied',
        description: `Paste it in ${browserName} to continue`,
      });
    } else {
      toast({
        title: 'Could not copy automatically',
        description: 'Long-press the address bar to copy this page URL.',
      });
    }
    void fireEvent(token, 'inapp_browser_copy_link', kind);
  };

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
    void fireEvent(token, 'inapp_browser_dismissed', kind);
  };

  // ---- Soft banner mode -----------------------------------------------------
  if (soft) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          style={{
            position: 'fixed',
            top: 'calc(8px + env(safe-area-inset-top))',
            left: 12,
            right: 12,
            zIndex: 9500,
            background: 'linear-gradient(160deg, #1f0a13, #2c0a1c)',
            border: '1px solid rgba(225,29,72,0.4)',
            borderRadius: 14,
            padding: '12px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
            maxWidth: 540,
            margin: '0 auto',
          }}
          data-testid="inapp-browser-overlay-soft"
        >
          <ExternalLink size={18} color="#e11d48" />
          <div style={{ flex: 1, color: 'rgba(255,255,255,0.92)', fontSize: 13, lineHeight: 1.4 }}>
            <strong style={{ color: '#fff' }}>Open in {browserName}</strong>{' '}
            for the full experience — tap the ••• menu.
          </div>
          <button
            onClick={handleDismiss}
            aria-label="Dismiss"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.6)',
              cursor: 'pointer',
              padding: 4,
            }}
            data-testid="inapp-browser-overlay-soft-dismiss"
          >
            <X size={16} />
          </button>
        </motion.div>
      </AnimatePresence>
    );
  }

  // ---- Full-screen hard mode ------------------------------------------------
  const instructions = getInstructionLines(kind);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          background: 'rgba(0,0,0,0.92)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          paddingTop: 'calc(24px + env(safe-area-inset-top))',
          paddingBottom: 'calc(24px + env(safe-area-inset-bottom))',
          overflowY: 'auto',
        }}
        data-testid="inapp-browser-overlay"
      >
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          style={{
            maxWidth: 360,
            width: '100%',
            padding: '32px 24px',
            borderRadius: 20,
            background: 'linear-gradient(160deg, #1f0a13, #2c0a1c)',
            border: '1px solid rgba(225,29,72,0.25)',
            boxShadow: '0 30px 60px rgba(0,0,0,0.6)',
            color: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: 18,
              background: 'rgba(225,29,72,0.12)',
              border: '1px solid rgba(225,29,72,0.35)',
              alignSelf: 'center',
            }}
            aria-hidden
          >
            <ExternalLink size={28} color="#e11d48" />
          </div>

          <div style={{ textAlign: 'center' }}>
            <h2
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 800,
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
              }}
            >
              Open in {browserName} to continue
            </h2>
            <p
              style={{
                marginTop: 10,
                marginBottom: 0,
                fontSize: 14,
                fontWeight: 500,
                lineHeight: 1.5,
                color: 'rgba(255,255,255,0.78)',
              }}
            >
              {appName} blocks notifications and matches. To get the full Papacito
              experience, open this page in your phone&apos;s main browser.
            </p>
          </div>

          <ol
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {instructions.map((line, idx) => (
              <li
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 12,
                  padding: '10px 12px',
                }}
              >
                <span
                  style={{
                    flex: '0 0 auto',
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: '#e11d48',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {idx + 1}
                </span>
                <span
                  style={{
                    fontSize: 14,
                    lineHeight: 1.4,
                    color: 'rgba(255,255,255,0.92)',
                    paddingTop: 3,
                  }}
                >
                  {line}
                </span>
              </li>
            ))}
          </ol>

          <button
            onClick={handleCopyLink}
            data-testid="inapp-browser-overlay-copy"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              width: '100%',
              padding: '14px 16px',
              borderRadius: 12,
              background: '#e11d48',
              color: '#ffffff',
              border: 'none',
              fontSize: 15,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(225,29,72,0.35)',
            }}
          >
            <Copy size={16} />
            Copy link
          </button>

          <button
            onClick={handleDismiss}
            data-testid="inapp-browser-overlay-dismiss"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.55)',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              padding: '6px 0',
              textDecoration: 'underline',
              textUnderlineOffset: 3,
              alignSelf: 'center',
            }}
          >
            Continue here anyway (limited features)
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default InAppBrowserOverlay;
